<template>
  <box
    class="anticipation-limits-card"
    :title="t('Limites de Antecipacao')"
    icon="pi pi-chart-bar"
  >
    <template #actions>
      <Button
        data-testid="anticipation-limits-refresh-button"
        :label="t('Atualizar')"
        icon="pi pi-refresh"
        class="p-button-text p-button-sm"
        :loading="isLoading"
        @click="fetchLimits"
      />
    </template>

    <!-- Loading state -->
    <div v-if="isLoading" class="loading-state">
      <spinner style="width: 50px; height: 50px" />
      <span class="loading-text">{{ t('Carregando limites...') }}</span>
    </div>

      <!-- Not supported state -->
      <div v-else-if="!limitsSupported" class="not-supported-state">
        <i class="pi pi-info-circle"></i>
        <span>{{ t('Este provedor nao fornece informacoes de limites de antecipacao') }}</span>
      </div>

      <!-- Error state -->
      <div v-else-if="errorMessage" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ errorMessage }}</span>
        <Button
          data-testid="anticipation-limits-retry-button"
          :label="t('Tentar novamente')"
          icon="pi pi-refresh"
          class="p-button-text p-button-sm"
          @click="fetchLimits"
        />
      </div>

      <!-- Limits content -->
      <div v-else-if="limits" class="limits-content">
        <!-- Available limit -->
        <div class="limit-item main-limit">
          <div class="limit-label">{{ t('Disponivel para Antecipar') }}</div>
          <div class="limit-value highlight">{{ formatCurrency(limits.available) }}</div>
        </div>

        <!-- Limit breakdown -->
        <div class="limits-breakdown">
          <div class="limit-item">
            <div class="limit-label">{{ t('Total Antecipavel') }}</div>
            <div class="limit-value">{{ formatCurrency(limits.total) }}</div>
          </div>
          <div class="limit-item">
            <div class="limit-label">{{ t('Em Uso') }}</div>
            <div class="limit-value">{{ formatCurrency(limits.used) }}</div>
          </div>
          <div class="limit-item">
            <div class="limit-label">{{ t('Pendente') }}</div>
            <div class="limit-value">{{ formatCurrency(limits.pending) }}</div>
          </div>
        </div>

        <!-- Progress bar -->
        <div class="usage-progress">
          <div class="progress-label">
            {{ t('Utilizacao') }}: {{ usagePercentage }}%
          </div>
          <ProgressBar
            :value="usagePercentage"
            :showValue="false"
            :style="{ height: '8px' }"
          />
        </div>

        <!-- Credit card anticipation info (if available) -->
        <div v-if="limits.creditCard" class="credit-card-section">
          <Divider />
          <div class="section-title">
            <i class="pi pi-credit-card mr-2"></i>
            {{ t('Cartao de Credito') }}
          </div>
          <div class="limit-item">
            <div class="limit-label">{{ t('Disponivel') }}</div>
            <div class="limit-value">{{ formatCurrency(limits.creditCard.available) }}</div>
          </div>
          <div v-if="limits.creditCard.automaticEnabled !== undefined" class="auto-anticipation">
            <Tag
              :value="limits.creditCard.automaticEnabled ? t('Automatica ativa') : t('Automatica inativa')"
              :severity="limits.creditCard.automaticEnabled ? 'success' : 'secondary'"
              :icon="limits.creditCard.automaticEnabled ? 'pi pi-check' : 'pi pi-times'"
            />
          </div>
        </div>

      <!-- Last updated -->
      <div v-if="lastUpdated" class="last-updated">
        {{ t('Atualizado em') }}: {{ formatDateTime(lastUpdated) }}
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { useAnticipations } from '../../composables/useAnticipations';

interface CreditCardLimits {
  available?: number;
  automaticEnabled?: boolean;
}

interface Limits {
  available?: number;
  total?: number;
  used?: number;
  pending?: number;
  creditCard?: CreditCardLimits;
}

interface LimitsResponse {
  supported: boolean;
  limits?: Limits;
}

const props = withDefaults(
  defineProps<{
    subaccountId: number;
    autoLoad?: boolean;
  }>(),
  {
    autoLoad: true,
  }
);

const emit = defineEmits<{
  (e: 'loaded', response: LimitsResponse): void;
  (e: 'error', err: unknown): void;
}>();

const { t } = useI18n();
const { getLimits } = useAnticipations();

// State
const isLoading = ref<boolean>(false);
const errorMessage = ref<string | null>(null);
const limits = ref<Limits | null>(null);
const limitsSupported = ref<boolean>(true);
const lastUpdated = ref<string | null>(null);

// Computed
const usagePercentage = computed<number>(() => {
  if (!limits.value || !limits.value.total) return 0;
  const used = limits.value.used || 0;
  const total = limits.value.total;
  return Math.round((used / total) * 100);
});

// Methods
const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR');
};

const fetchLimits = async (): Promise<void> => {
  log('AnticipationLimitsCard', 'fetchLimits', 'Fetching limits', { subaccountId: props.subaccountId });
  isLoading.value = true;
  errorMessage.value = null;

  try {
    const response = await getLimits(props.subaccountId) as unknown as LimitsResponse;

    if (!response.supported) {
      limitsSupported.value = false;
      limits.value = null;
    } else {
      limitsSupported.value = true;
      limits.value = response.limits || null;
      lastUpdated.value = new Date().toISOString();
    }

    emit('loaded', response);
  } catch (err) {
    log('AnticipationLimitsCard', 'fetchLimits', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    errorMessage.value = message || t('Erro ao carregar limites');
    emit('error', err);
  } finally {
    isLoading.value = false;
  }
};

// Watch for subaccount changes
watch(() => props.subaccountId, (newId) => {
  if (newId && props.autoLoad) {
    fetchLimits();
  }
});

// Load on mount if autoLoad enabled
onMounted(() => {
  if (props.subaccountId && props.autoLoad) {
    fetchLimits();
  }
});

// Expose method for parent components
defineExpose({
  refresh: fetchLimits
});
</script>

<style scoped>
.anticipation-limits-card {
  height: 100%;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
}

.loading-state,
.not-supported-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.not-supported-state i,
.error-state i {
  font-size: 2rem;
}

.limits-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.limit-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.main-limit {
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 8px;
}

.main-limit .limit-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.main-limit .limit-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.limit-value.highlight {
  color: var(--primary-color);
}

.limits-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.limit-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.limit-value {
  font-weight: 500;
}

.usage-progress {
  margin-top: 0.5rem;
}

.progress-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  margin-bottom: 0.25rem;
}

.credit-card-section {
  padding-top: 0.5rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.auto-anticipation {
  margin-top: 0.5rem;
}

.last-updated {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  text-align: right;
  margin-top: 0.5rem;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
